/**
 * useNotifications Hook
 *
 * Unified notification system that composes invite and trade notifications
 * into a single interface for the notification dropdown.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useInviteNotifications } from './useInviteNotifications';
import { useTradeNotifications } from './useTradeNotifications';
import type { InviteNotification, TradeNotification } from '@drawn-of-war/shared';

/**
 * Notification action that can be performed on a notification
 */
export interface NotificationAction {
  label: string;
  type: 'accept' | 'decline' | 'view' | 'counter';
  variant: 'primary' | 'secondary' | 'danger';
}

/**
 * Unified notification type that normalizes invites and trades
 */
export interface UnifiedNotification {
  id: string;
  type: 'invite' | 'trade';
  subtype: string;
  title: string;
  message: string;
  timestamp: string;
  expiresAt?: string;
  isRead: boolean;
  isYourTurn: boolean;
  data: InviteNotification | TradeNotification;
  actions: NotificationAction[];
}

/**
 * Hook return type
 */
export interface UseNotificationsResult {
  /** All notifications sorted by timestamp */
  notifications: UnifiedNotification[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Loading state for any action */
  isLoading: boolean;
  /** Error message if any action failed */
  error: string | null;
  /** Mark a notification as read */
  markAsRead: (id: string) => void;
  /** Mark all notifications as read */
  markAllAsRead: () => void;
  /** Accept an invite */
  acceptInvite: (inviteId: string) => Promise<void>;
  /** Decline an invite */
  declineInvite: (inviteId: string) => Promise<void>;
  /** Accept a trade */
  acceptTrade: (tradeId: string) => Promise<void>;
  /** Decline a trade */
  declineTrade: (tradeId: string) => Promise<void>;
  /** Clear error */
  clearError: () => void;
}

/** LocalStorage key for read notification IDs */
const STORAGE_KEY = 'dow_notifications_read_v1';

/**
 * Load read IDs from localStorage
 */
function loadReadIds(): Set<string> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Set(parsed.ids || []);
    }
  } catch {
    // Ignore parse errors
  }
  return new Set();
}

/**
 * Persist read IDs to localStorage
 */
function persistReadIds(ids: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ids: Array.from(ids),
      lastUpdated: new Date().toISOString()
    }));
  } catch {
    // Ignore storage errors (e.g., incognito mode)
  }
}

/**
 * Transform an invite to a unified notification
 */
function transformInvite(invite: InviteNotification, readIds: Set<string>): UnifiedNotification {
  const isChallenge = invite.type === 'challenge';
  const notificationId = `invite-${invite.id}`;

  return {
    id: notificationId,
    type: 'invite',
    subtype: invite.type,
    title: isChallenge ? 'Battle Challenge' : 'Lobby Invite',
    message: isChallenge
      ? `${invite.from.displayName} challenged you to battle!`
      : `${invite.from.displayName} invited you to their lobby`,
    timestamp: invite.createdAt,
    expiresAt: invite.expiresAt,
    isRead: readIds.has(notificationId),
    isYourTurn: true, // Invites are always actionable by recipient
    data: invite,
    actions: [
      { label: 'Accept', type: 'accept', variant: 'primary' },
      { label: 'Decline', type: 'decline', variant: 'secondary' }
    ]
  };
}

/**
 * Transform a trade to a unified notification
 */
function transformTrade(trade: TradeNotification, readIds: Set<string>): UnifiedNotification {
  const notificationId = `trade-${trade.id}`;

  let title: string;
  let message: string;

  switch (trade.type) {
    case 'offer':
      title = 'Trade Offer';
      message = `${trade.withUser.displayName} wants to trade`;
      break;
    case 'counter':
      title = 'Counter Offer';
      message = `${trade.withUser.displayName} made a counter offer`;
      break;
    default:
      title = 'Trade Update';
      message = `Trade with ${trade.withUser.displayName}`;
  }

  // Build actions based on whose turn it is
  const actions: NotificationAction[] = [];
  if (trade.isYourTurn) {
    actions.push(
      { label: 'View', type: 'view', variant: 'primary' },
      { label: 'Decline', type: 'decline', variant: 'secondary' }
    );
  } else {
    actions.push({ label: 'View', type: 'view', variant: 'secondary' });
  }

  return {
    id: notificationId,
    type: 'trade',
    subtype: trade.type,
    title,
    message,
    timestamp: trade.expiresAt, // Use expiresAt as timestamp for sorting
    expiresAt: trade.expiresAt,
    isRead: readIds.has(notificationId),
    isYourTurn: trade.isYourTurn,
    data: trade,
    actions
  };
}

/**
 * useNotifications Hook
 *
 * Composes invite and trade notifications into a unified interface.
 * Provides read state tracking persisted to localStorage.
 */
export function useNotifications(): UseNotificationsResult {
  // Compose existing hooks
  const inviteHook = useInviteNotifications();
  const tradeHook = useTradeNotifications();

  // Read state tracking
  const [readIds, setReadIds] = useState<Set<string>>(() => loadReadIds());

  // Transform and merge notifications
  const notifications = useMemo(() => {
    const all: UnifiedNotification[] = [
      ...inviteHook.invites.map(invite => transformInvite(invite, readIds)),
      ...tradeHook.trades.map(trade => transformTrade(trade, readIds))
    ];

    // Sort by timestamp (newest first)
    return all.sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });
  }, [inviteHook.invites, tradeHook.trades, readIds]);

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.isRead).length;
  }, [notifications]);

  // Combined loading state
  const isLoading = inviteHook.isLoading || tradeHook.isLoading;

  // Combined error (prefer invite error, then trade)
  const error = inviteHook.error || tradeHook.error;

  // Clear error from both hooks
  const clearError = useCallback(() => {
    inviteHook.clearError();
    tradeHook.clearError();
  }, [inviteHook, tradeHook]);

  // Mark a single notification as read
  const markAsRead = useCallback((id: string) => {
    setReadIds(prev => {
      const next = new Set(prev);
      next.add(id);
      persistReadIds(next);
      return next;
    });
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(() => {
    const allIds = notifications.map(n => n.id);
    const next = new Set(allIds);
    setReadIds(next);
    persistReadIds(next);
  }, [notifications]);

  // Cleanup stale read IDs when notifications change
  useEffect(() => {
    const currentIds = new Set(notifications.map(n => n.id));
    setReadIds(prev => {
      // Keep only IDs that still have matching notifications
      const pruned = new Set([...prev].filter(id => currentIds.has(id)));
      if (pruned.size !== prev.size) {
        persistReadIds(pruned);
        return pruned;
      }
      return prev;
    });
  }, [notifications]);

  // Accept invite wrapper (extracts original ID)
  const acceptInvite = useCallback(async (notificationId: string) => {
    const originalId = notificationId.replace('invite-', '');
    await inviteHook.acceptInvite(originalId);
  }, [inviteHook]);

  // Decline invite wrapper
  const declineInvite = useCallback(async (notificationId: string) => {
    const originalId = notificationId.replace('invite-', '');
    await inviteHook.declineInvite(originalId);
  }, [inviteHook]);

  // Accept trade wrapper
  const acceptTrade = useCallback(async (notificationId: string) => {
    const originalId = notificationId.replace('trade-', '');
    await tradeHook.acceptTrade(originalId);
  }, [tradeHook]);

  // Decline trade wrapper
  const declineTrade = useCallback(async (notificationId: string) => {
    const originalId = notificationId.replace('trade-', '');
    await tradeHook.declineTrade(originalId);
  }, [tradeHook]);

  return {
    notifications,
    unreadCount,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    acceptInvite,
    declineInvite,
    acceptTrade,
    declineTrade,
    clearError
  };
}

/**
 * Format relative time for notification display
 */
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return date.toLocaleDateString();
}
